/**
 * src/components/Financial/modules/PlanetaryAspectModule.jsx
 */
import React from "react";
import * as Astro from "../../../astronomy.js";
import { PLANET_SYMBOLS } from "../../../utils/constants.js";
import { Zap, Info } from "lucide-react";

// --- ヘルパー関数: アスペクト角度からシンボルを取得 ---
const getAspectSymbol = (angle) => {
  switch (angle) {
    case 0:
      return "☌";
    case 45:
      return "∠";
    case 60:
      return "⚹";
    case 90:
      return "□";
    case 120:
      return "△";
    case 135:
      return "⚼";
    case 180:
      return "☍";
    default:
      return "";
  }
};

// --- 1. 計算ロジック (ピーク検出) ---
export const calculatePlanetaryAspects = (
  timeline,
  config,
  isHeliocentric,
  isSidereal,
) => {
  if (
    !timeline ||
    timeline.length === 0 ||
    !config ||
    !Array.isArray(config.aspects)
  )
    return [];

  const allRawHits = [];
  const { p1, p2, orb, aspects } = config;

  // 全期間スキャン
  timeline.forEach((d, idx) => {
    const date = d.timestamp ? new Date(d.timestamp) : new Date(d.Date);
    const lon1 = isHeliocentric
      ? Astro.getHeliocentricLongitude(p1, date)
      : Astro.getPlanetLongitude(p1, date, isSidereal);
    const lon2 = isHeliocentric
      ? Astro.getHeliocentricLongitude(p2, date)
      : Astro.getPlanetLongitude(p2, date, isSidereal);
    if (lon1 === null || lon2 === null) return;

    let diff = Math.abs(lon1 - lon2);
    if (diff > 180) diff = 360 - diff;

    aspects.forEach((targetAngle) => {
      const currentOrb = Math.abs(diff - targetAngle);
      if (currentOrb <= orb) {
        allRawHits.push({
          index: idx,
          angle: targetAngle,
          orb: currentOrb,
          planet1: p1,
          planet2: p2,
        });
      }
    });
  });

  // グループ化とピーク検出
  allRawHits.sort((a, b) => a.angle - b.angle || a.index - b.index);
  const uniqueHits = [];
  const processed = new Set();

  allRawHits.forEach((hit, i) => {
    if (processed.has(i)) return;

    const group = [hit];
    processed.add(i);

    let next = i + 1;
    while (next < allRawHits.length) {
      const nextHit = allRawHits[next];
      const lastInGroup = group[group.length - 1];
      if (
        nextHit.angle === hit.angle &&
        nextHit.index <= lastInGroup.index + 3
      ) {
        group.push(nextHit);
        processed.add(next);
        next++;
      } else {
        break;
      }
    }

    let peakHit = group[0];
    group.forEach((h) => {
      if (h.orb < peakHit.orb) peakHit = h;
    });

    uniqueHits.push({
      ...peakHit,
      startIndex: group[0].index,
      endIndex: group[group.length - 1].index,
    });
  });

  return uniqueHits;
};

// --- 2. 背景描画レイヤー ---
export const PlanetaryAspectLayer = ({
  show,
  hits,
  coords,
  displayRange,
  isDarkMode,
}) => {
  if (!show || !hits || hits.length === 0 || !coords) return null;

  const { getX, pad, iH, actualWidth } = coords;
  const { start, count } = displayRange;

  return (
    <g className="planetary-aspect-bg-layer" opacity={isDarkMode ? 0.25 : 0.15}>
      {hits.map((hit, i) => {
        if (hit.endIndex < start || hit.startIndex >= start + count)
          return null;

        const colors = {
          0: "#f06292",
          45: "#ffb74d",
          60: "#3498db",
          90: "#4db6ac",
          120: "#2ecc71",
          135: "#7986cb",
          180: "#9575cd",
        };
        const color = colors[hit.angle] || "#6366f1";
        const unitW = Math.max(2, (actualWidth - pad.l - pad.r) / count);
        const xStart = getX(hit.startIndex - start);
        const xEnd = getX(hit.endIndex - start);
        const rectX = xStart - unitW / 2;
        const rectW = xEnd - xStart + unitW;

        return (
          <rect
            key={`pa-bg-${i}`}
            x={rectX}
            y={pad.t}
            width={Math.max(2, rectW)}
            height={iH}
            fill={color}
          />
        );
      })}
    </g>
  );
};

// --- 3. アイコン描画レイヤー (デザイン変更) ---
export const PlanetaryAspectIconsLayer = ({
  show,
  hits,
  coords,
  displayRange,
  isDarkMode,
  planetColors,
}) => {
  if (!show || !hits || hits.length === 0 || !coords) return null;

  const { getX, pad, iH } = coords;
  const { start, count } = displayRange;

  // デザイン設定
  const planetIconSize = 16; // 惑星アイコンのサイズ
  const aspectIconSize = 24; // アスペクトアイコンのサイズ (大きく)
  const planetGap = 10; // 惑星間の横方向の間隔
  const verticalGap = 20; // 惑星の列とアスペクトの列の間隔

  // 基準Y座標 (チャート下端から少し離す)
  const baseY = pad.t + iH + 20;

  return (
    <g className="planetary-aspect-icons-layer" pointerEvents="none">
      {hits.map((hit, i) => {
        if (hit.index < start || hit.index >= start + count) return null;

        const x = getX(hit.index - start);
        const aspectSymbol = getAspectSymbol(hit.angle);
        const p1Color =
          planetColors?.[hit.planet1] || (isDarkMode ? "#fff" : "#000");
        const p2Color =
          planetColors?.[hit.planet2] || (isDarkMode ? "#fff" : "#000");
        const aspectColor = isDarkMode ? "#ccc" : "#555";

        return (
          <g key={`pa-ico-${i}`} transform={`translate(${x}, ${baseY})`}>
            {/* 1段目: 惑星アイコン2つを並列表示 */}
            <g transform={`translate(0, 0)`}>
              {/* 左側の惑星 */}
              <text
                x={-planetGap / 2}
                y={0}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={planetIconSize}
                fill={p1Color}
                fontWeight="bold"
              >
                {PLANET_SYMBOLS[hit.planet1]}
              </text>
              {/* 右側の惑星 */}
              <text
                x={planetGap / 2}
                y={0}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={planetIconSize}
                fill={p2Color}
                fontWeight="bold"
              >
                {PLANET_SYMBOLS[hit.planet2]}
              </text>
            </g>

            {/* 2段目: アスペクトアイコン (大きく太字で) */}
            <text
              x={0}
              y={verticalGap}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={aspectIconSize} // サイズ大
              fill={aspectColor}
              fontWeight="bold" // 太字
            >
              {aspectSymbol}
            </text>
          </g>
        );
      })}
    </g>
  );
};

// --- 4. 設定UI (変更なし) ---
export const PlanetaryAspectSettingsUI = ({
  show,
  setShow,
  config,
  setConfig,
  isDarkMode,
  isHeliocentric,
}) => {
  const isDark =
    isDarkMode === true || isDarkMode === "true" || isDarkMode === "dark";
  const planets = isHeliocentric
    ? [
        "Earth",
        "Mercury",
        "Venus",
        "Mars",
        "Jupiter",
        "Saturn",
        "Uranus",
        "Neptune",
        "Pluto",
        "NorthNode",
        "Chiron",
      ]
    : [
        "Sun",
        "Moon",
        "Mercury",
        "Venus",
        "Mars",
        "Jupiter",
        "Saturn",
        "Uranus",
        "Neptune",
        "Pluto",
        "NorthNode",
      ];

  const toggleAspect = (angle) => {
    const currentAspects = Array.isArray(config.aspects) ? config.aspects : [];
    const newAspects = currentAspects.includes(angle)
      ? currentAspects.filter((a) => a !== angle)
      : [...currentAspects, angle];
    setConfig({ ...config, aspects: newAspects });
  };

  const btnSt = (active) => ({
    padding: "6px 20px",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    cursor: "pointer",
    background: active ? "#2c2d96" : isDark ? "#2d2c33" : "#ffffff",
    color: active ? "#fff" : isDark ? "#ccc" : "#64748b",
    border:
      "1px solid " +
      (active ? "#2c2d96" : isDark ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
    transition: "all 0.2s",
  });

  return (
    <div
      style={{
        background: isDark ? "#1c1f26" : "#f8fafc",
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + (isDark ? "#2d313d" : "#e2e8f0"),
        marginBottom: "15px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "bold",
            color: isDark ? "#fff" : "#1e293b",
          }}
        >
          <Zap size={18} color={isDark ? "#ffffff" : "#2c2d96"} />
          <span>天体アスペクト</span>
        </div>
        <button onClick={() => setShow(!show)} style={btnSt(show)}>
          {show ? "ON" : "OFF"}
        </button>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
        }}
      >
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{
                fontSize: "11px",
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              天体 1
            </span>
            <select
              style={{
                padding: "8px",
                background: isDark ? "#0b0e14" : "#fff",
                color: isDark ? "#fff" : "#000",
                border: "1px solid " + (isDark ? "#374151" : "#cbd5e1"),
                borderRadius: "6px",
                fontSize: "12px",
              }}
              value={config.p1}
              onChange={(e) => setConfig({ ...config, p1: e.target.value })}
            >
              {planets.map((p) => (
                <option key={p} value={p}>
                  {p} {PLANET_SYMBOLS[p]}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{
                fontSize: "11px",
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              天体 2
            </span>
            <select
              style={{
                padding: "8px",
                background: isDark ? "#0b0e14" : "#fff",
                color: isDark ? "#fff" : "#000",
                border: "1px solid " + (isDark ? "#374151" : "#cbd5e1"),
                borderRadius: "6px",
                fontSize: "12px",
              }}
              value={config.p2}
              onChange={(e) => setConfig({ ...config, p2: e.target.value })}
            >
              {planets.map((p) => (
                <option key={p} value={p}>
                  {p} {PLANET_SYMBOLS[p]}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span
              style={{
                fontSize: "11px",
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              オーブ
            </span>
            <input
              type="number"
              step="0.1"
              style={{
                padding: "8px",
                background: isDark ? "#0b0e14" : "#fff",
                color: isDark ? "#fff" : "#000",
                border: "1px solid " + (isDark ? "#374151" : "#cbd5e1"),
                borderRadius: "6px",
                fontSize: "12px",
                width: "60px",
              }}
              value={config.orb}
              onChange={(e) =>
                setConfig({ ...config, orb: parseFloat(e.target.value) || 0 })
              }
            />
          </div>
        </div>
        <div>
          <span
            style={{
              fontSize: "11px",
              color: isDark ? "#9ca3af" : "#64748b",
              display: "block",
              marginBottom: "5px",
            }}
          >
            対象角度
          </span>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {[0, 45, 60, 90, 120, 135, 180].map((angle) => {
              const isActive =
                Array.isArray(config.aspects) && config.aspects.includes(angle);
              return (
                <button
                  key={angle}
                  onClick={() => toggleAspect(angle)}
                  style={{
                    padding: "4px 10px",
                    fontSize: "11px",
                    borderRadius: "4px",
                    border:
                      "1px solid " +
                      (isActive ? "#2962ff" : isDark ? "#374151" : "#ccc"),
                    background: isActive ? "#2962ff" : "transparent",
                    color: isActive ? "#fff" : isDark ? "#ccc" : "#666",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {angle}°
                </button>
              );
            })}
          </div>
        </div>{" "}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            // theme.subText に相当する色設定
            color: isDark ? "rgba(255, 255, 255, 0.4)" : "#64748b",
            fontSize: "10px",
            marginTop: "20px",
            paddingTop: "15px",
            // theme.border に相当する色設定
            borderTop: "1px solid " + (isDark ? "#2d313d" : "#e2e8f0"),
          }}
        >
          <Info size={14} />
          <span>選択した天体のアスペクト形成ゾーンを表示します。</span>
        </div>
      </div>
    </div>
  );
};
